import React from 'react';
import { string, oneOf, oneOfType, bool, object } from 'prop-types';
import classNames from 'classnames';
import { useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import {
  ensureUser,
  ensureCurrentUser,
  userDisplayNameAsString,
  userAbbreviatedName,
  getUserTier,
} from '../../util/data';
import { Icon, ResponsiveImage, NamedLink } from '../../components/';

import css from './Avatar.module.css';

// Responsive image sizes hint
const AVATAR_SIZES = '40px';
const AVATAR_SIZES_MEDIUM = '60px';
const AVATAR_SIZES_LARGE = '96px';

// The board's sizes (ComponentsMore: avatars), in px.
export const AVATAR_BOARD_SIZES = [64, 48, 40, 32, 30, 24];

const AVATAR_IMAGE_VARIANTS = [
  // 40x40
  'square-xsmall',

  // 80x80
  'square-xsmall2x',

  // 240x240
  'square-small',

  // 480x480
  'square-small2x',

  'default',
];

/**
 * A person's (round) or an organization's (12px corners) picture, or their initials.
 *
 * `size` picks one of the board's sizes (64/48/40/32/30/24). Without it the avatar is 40px, and a
 * caller's `className` can still set its own width and height, as before.
 */
const Avatar = props => {
  const intl = useIntl();
  const {
    rootClassName = null,
    className = null,
    initialsClassName = null,
    user = null,
    size = null,
    organization = false,
    renderSizes: renderSizesProp = null,
    useUserTier = false,
    disableProfileLink = false,
    useCreatorAvatar = false,
  } = props;

  const userIsCurrentUser = user && user.type === 'currentUser';
  const avatarUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);

  const avatarUserPoints = avatarUser.attributes.profile?.metadata?.points;
  const avatarUserTier = getUserTier(avatarUserPoints);

  const classes = classNames(rootClassName || css.root, className, {
    [css[`size${size}`]]: !!size,
    [css.sized]: !!size,
    [css.organization]: organization,
    [css[avatarUserTier?.avatarClassName]]: useUserTier && avatarUserTier,
  });
  const renderSizes = renderSizesProp || (size ? `${size}px` : AVATAR_SIZES);

  const isBannedUser = avatarUser.attributes.banned;
  const isDeletedUser = avatarUser.attributes.deleted;

  const defaultUserDisplayName = isBannedUser
    ? intl.formatMessage({ id: 'Avatar.bannedUserDisplayName' })
    : isDeletedUser
    ? intl.formatMessage({ id: 'Avatar.deletedUserDisplayName' })
    : '';

  const defaultUserAbbreviatedName = '';

  const displayName = userDisplayNameAsString(avatarUser, defaultUserDisplayName);
  const abbreviatedName = userAbbreviatedName(avatarUser, defaultUserAbbreviatedName);
  const rootProps = { className: classes, title: displayName };
  const linkProps = avatarUser.id
    ? { name: 'ProfilePage', params: { id: avatarUser.id.uuid } }
    : { name: 'ProfileBasePage' };

  // An image link from publicData: the creator profile image when
  // useCreatorAvatar is set, otherwise the member avatar kept in our own
  // storage (util/memberAvatar.js). Either comes before the Sharetribe profile
  // image, which is billed per request and per unique image each month.
  const avatarPublicData = avatarUser.attributes?.profile?.publicData || {};
  const creatorImage = useCreatorAvatar ? avatarPublicData.profileImage : null;
  const customArtistImage = creatorImage?.link ? creatorImage : avatarPublicData.avatarImage;
  const hasCustomArtistImage = customArtistImage && customArtistImage.link;

  const hasProfileImage = avatarUser.profileImage && avatarUser.profileImage.id;
  const profileLinkEnabled = !disableProfileLink;

  const classForInitials = initialsClassName || css.initials;
  // No initials (no user yet, or a profile without a name): a person outline instead of a blank
  // disc.
  const placeholder = abbreviatedName ? (
    <span className={classForInitials}>{abbreviatedName}</span>
  ) : (
    <Icon name="person" size={24} className={css.placeholderIcon} />
  );

  if (isBannedUser || isDeletedUser) {
    return (
      <div {...rootProps}>
        <Icon name="banned" size={24} className={css.bannedUserIcon} />
      </div>
    );
  } else if (hasCustomArtistImage && profileLinkEnabled) {
    return (
      <NamedLink {...rootProps} {...linkProps}>
        <img
          className={css.avatarImage}
          src={customArtistImage.link}
          loading="lazy"
          decoding="async"
          alt={displayName}
        />
      </NamedLink>
    );
  } else if (hasCustomArtistImage) {
    return (
      <div {...rootProps}>
        <img
          className={css.avatarImage}
          src={customArtistImage.link}
          loading="lazy"
          decoding="async"
          alt={displayName}
        />
      </div>
    );
  } else if (hasProfileImage && profileLinkEnabled) {
    return (
      <NamedLink {...rootProps} {...linkProps}>
        <ResponsiveImage
          rootClassName={css.avatarImage}
          alt={displayName}
          image={avatarUser.profileImage}
          variants={AVATAR_IMAGE_VARIANTS}
          sizes={renderSizes}
        />
      </NamedLink>
    );
  } else if (hasProfileImage) {
    return (
      <div {...rootProps}>
        <ResponsiveImage
          rootClassName={css.avatarImage}
          alt={displayName}
          image={avatarUser.profileImage}
          variants={AVATAR_IMAGE_VARIANTS}
          sizes={renderSizes}
        />
      </div>
    );
  } else if (profileLinkEnabled) {
    // Placeholder avatar (initials)
    return (
      <NamedLink {...rootProps} {...linkProps}>
        {placeholder}
      </NamedLink>
    );
  } else {
    // Placeholder avatar (initials)
    return <div {...rootProps}>{placeholder}</div>;
  }
};

Avatar.propTypes = {
  rootClassName: string,
  className: string,
  initialsClassName: string,
  user: oneOfType([propTypes.user, propTypes.currentUser, object]),

  // One of the board's sizes; without it the avatar is 40px (or what `className` sets)
  size: oneOf(AVATAR_BOARD_SIZES),
  // Organizations have 12px corners instead of a circle
  organization: bool,
  // The `sizes` hint for the image; defaults to the avatar's size
  renderSizes: string,
  disableProfileLink: bool,
  useUserTier: bool,
  useCreatorAvatar: bool,
};

export default Avatar;

// The template's named sizes. AvatarSmall is the board's 32px.
export const AvatarSmall = props => <Avatar size={32} {...props} />;
AvatarSmall.displayName = 'AvatarSmall';

export const AvatarMedium = props => (
  <Avatar rootClassName={css.mediumAvatar} renderSizes={AVATAR_SIZES_MEDIUM} {...props} />
);
AvatarMedium.displayName = 'AvatarMedium';

export const AvatarLarge = props => (
  <Avatar rootClassName={css.largeAvatar} renderSizes={AVATAR_SIZES_LARGE} {...props} />
);
AvatarLarge.displayName = 'AvatarLarge';
